// Pure-data loader for the wooden-boat trimesh collider. Imports no
// THREE — the sim layer (server + client) consumes this directly so the
// physics build-out doesn't pull a renderer dependency chain.
//
// Source: scripts/bake-boat-collision.mjs writes public/boat-collision.json
// from the wooden-boat GLB. Re-run that script when the GLB or the
// scale / centre / rotation pipeline in WoodenBoatAssets.ts changes —
// the bake script and the runtime loader must agree vertex-for-vertex.
//
// Browser load: fetch('/boat-collision.json').
// Node load:    fs.readFile('public/boat-collision.json') (server boot
//               does this before the first GameRoom is ever created).

export interface BoatCollisionMesh {
  vertices: Float32Array;
  indices: Uint32Array;
}

interface CollisionJson {
  vertices: number[];
  indices: number[];
  meta?: unknown;
}

let _loaded: BoatCollisionMesh | null = null;
let _loadPromise: Promise<BoatCollisionMesh> | null = null;

// TEMPORARY: submarine swap. Revert these two constants to
// '/boat-collision.json' / 'public/boat-collision.json' to restore the
// wooden boat. WoodenBoatAssets.ts MODEL_PATH must be reverted to match.
const JSON_URL = '/sub-collision.json';
const NODE_FS_PATH = 'public/sub-collision.json';

function toTypedArrays(json: CollisionJson): BoatCollisionMesh {
  return {
    vertices: new Float32Array(json.vertices),
    indices: new Uint32Array(json.indices)
  };
}

async function loadBrowser(): Promise<BoatCollisionMesh> {
  const res = await fetch(JSON_URL);
  if (!res.ok) {
    throw new Error(
      `[BoatCollisionData] fetch ${JSON_URL} failed: ${res.status} ${res.statusText}. ` +
      `Did you run \`node scripts/bake-boat-collision.mjs\`?`
    );
  }
  const json = (await res.json()) as CollisionJson;
  return toTypedArrays(json);
}

// Same Node-builtin wrappers used in shipCollision.ts. Local declarations
// because this file must compile under a DOM-only tsconfig (the client
// build on Vercel doesn't pull in @types/node). At runtime these only
// run on the server — the browser takes the fetch path.
interface NodeFsPromises {
  readFile(path: string, encoding: string): Promise<string>;
}
interface NodePath {
  resolve(...parts: string[]): string;
  dirname(p: string): string;
}
interface NodeUrl {
  fileURLToPath(url: string): string;
}
interface NodeProcess {
  versions?: { node?: string };
  cwd(): string;
}

function getNodeProcess(): NodeProcess | undefined {
  return (globalThis as { process?: NodeProcess }).process;
}

async function loadNode(): Promise<BoatCollisionMesh> {
  // Stash specifiers through a function arg so TS treats them as generic
  // strings (Vercel's client build has no @types/node).
  const dyn = (spec: string): Promise<unknown> => import(/* @vite-ignore */ spec);
  const fs = (await dyn('node:fs/promises')) as NodeFsPromises;
  const path = (await dyn('node:path')) as NodePath;
  const url = (await dyn('node:url')) as NodeUrl;
  const proc = getNodeProcess();
  const cwd = proc?.cwd() ?? '.';
  const cwdCandidate = path.resolve(cwd, NODE_FS_PATH);
  const parentCandidate = path.resolve(cwd, '..', NODE_FS_PATH);
  const moduleCandidate = (() => {
    try {
      const here = path.dirname(url.fileURLToPath(import.meta.url));
      // boatCollision.ts lives at src/maps/bundles/sinking-ship/ — that's
      // four levels below the repo root, where public/ lives.
      return path.resolve(here, '..', '..', '..', '..', NODE_FS_PATH);
    } catch {
      return cwdCandidate;
    }
  })();
  const candidates = [cwdCandidate, parentCandidate, moduleCandidate];
  let lastErr: unknown = null;
  for (const p of candidates) {
    try {
      const buf = await fs.readFile(p, 'utf8');
      const json = JSON.parse(buf) as CollisionJson;
      return toTypedArrays(json);
    } catch (err) {
      lastErr = err;
    }
  }
  throw new Error(
    `[BoatCollisionData] could not read ${NODE_FS_PATH} from any of ` +
    `[${candidates.join(', ')}]. Did you run \`node scripts/bake-boat-collision.mjs\`? ` +
    `Last error: ${String(lastErr)}`
  );
}

/** Kick off the collision-data load. Idempotent. Call from the server's
 *  boot path and from the client's sinkingShip preload path. */
export function preloadBoatCollision(): Promise<BoatCollisionMesh> {
  if (_loadPromise) return _loadPromise;
  const proc = getNodeProcess();
  const isNode = !!(proc && proc.versions && proc.versions.node);
  _loadPromise = (isNode ? loadNode() : loadBrowser()).then((m) => {
    _loaded = m;
    return m;
  });
  return _loadPromise;
}

/** Synchronous accessor. Returns null if preload hasn't finished. */
export function getBoatCollision(): BoatCollisionMesh | null {
  return _loaded;
}
